// React
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
	UserRoundIcon,
	UsersRoundIcon,
	ChevronDownIcon,
	MapPinIcon,
	TriangleAlertIcon,
} from "lucide-react";

// Components
import {
	getEndTime,
	transformStartTime,
} from "../../../components/formattedTime";
import ContinueBtn from "../../../components/ContinueBtn";
import ProgressStepper from "../../../components/ProgressStepper";
import logo from "../../../images/logoAndSponsor.png";
import {
	getAvailableBooths,
	temproraryReservation,
	getLocation,
} from "../../../services/api";
import { capitalizeFirstLetter } from "../../../utils/textFormatter";
import { checkPastTime } from "../../../utils/timeFormatter";

// Env
const env = process.env.REACT_APP_ENV;

export default function SelectBox() {
	const navigate = useNavigate();
	const location = useLocation();
	const { locationId } = useParams();
	const { startTime, duration, date, boothId, preSelectedBooth } =
		location.state || {};

	if (!startTime || !duration)
		navigate(`/${locationId}/reservation/time-scheduler`);
	if (checkPastTime(startTime, locationId))
		navigate(`/${locationId}/reservation/time-scheduler`);

	const [errorMessage, setErrorMessage] = useState(false);
	const [selectedBooth, setSelectedBooth] = useState(null);
	const [selectedBuilding, setSelectedBuilding] = useState(null);
	const [pageLoading, setPageLoading] = useState(false);
	const [availableBooths, setAvailableBooths] = useState([]);
	const [groupedBuildings, setGroupedBuildings] = useState([]);
	const [boothType, setBoothType] = useState("single");
	const [myLocation, setMyLocation] = useState(null);
	const [boothLocation, setBoothLocation] = useState(null);

	// Add a ref to store the timeout ID
	const errorTimeoutRef = useRef(null);

	// Add a new ref to track initial load
	const isInitialLoad = useRef(true);

	// Update setErrorMessage to handle timeout
	const displayErrorMessage = (message) => {
		// Clear any existing timeout to reset the error message
		if (errorTimeoutRef.current) {
			clearTimeout(errorTimeoutRef.current);
		}

		setErrorMessage(message);

		// Set a new timeout to clear the error message after 7 seconds
		errorTimeoutRef.current = setTimeout(() => {
			setErrorMessage(false);
			errorTimeoutRef.current = null; // Clear the ref once the timeout is done
		}, 7000);
	};

	// Define fetchBooths outside of useEffect to make it accessible throughout the component
	const fetchBooths = async () => {
		setPageLoading(true);
		try {
			const booths = await getAvailableBooths({
				startTime,
				duration,
				boothType,
				locationId,
				date,
				boothId,
			});
			console.log("booths", booths);

			if (booths.message === "No available booths found at this time") {
				setAvailableBooths([]);
			} else {
				let processedBooths = [...booths.availableBooths];
				// First, check if we have a boothId in any of the available booths
				const scannedAvailableBooth = processedBooths.find(
					(booth) => Number(booth.boothId) === Number(boothId)
				);
				if (scannedAvailableBooth) {
					setMyLocation(scannedAvailableBooth.boxPlacement);
					setSelectedBuilding(scannedAvailableBooth.boxPlacement);
				}

				// Then check for locked scanned booth
				if (
					booths.scannedBooth &&
					(booths.scannedBooth.status === "locked" ||
						booths.scannedBooth.status === "reserved") &&
					booths.scannedBooth.booth.boothType === boothType
				) {
					const scannedBooth = {
						...booths.scannedBooth.booth,
						isScannedBooth: true,
						isSelectable: false,
						timeSlotStatus: booths.scannedBooth.status,
					};
					processedBooths.push(scannedBooth);

					setMyLocation(booths.scannedBooth.booth.boxPlacement);
					setSelectedBuilding(booths.scannedBooth.booth.boxPlacement);
				}

				// If we have a boothId and this is initial load, check both booth types
				if (isInitialLoad.current && boothId) {
					const singleBooths =
						boothType === "single"
							? processedBooths
							: await getAvailableBooths({
									startTime,
									duration,
									boothType: "single",
									locationId,
									date,
							  });
					const multiBooths =
						boothType === "multi"
							? processedBooths
							: await getAvailableBooths({
									startTime,
									duration,
									boothType: "multi",
									locationId,
									date,
							  });

					const isInSingle = singleBooths.availableBooths?.some(
						(booth) => Number(booth.boothId) === Number(boothId)
					);
					const isInMulti = multiBooths.availableBooths?.some(
						(booth) => Number(booth.boothId) === Number(boothId)
					);

					if (isInMulti || isInSingle) {
						const targetBooth = isInMulti
							? multiBooths.availableBooths.find(
									(booth) => Number(booth.boothId) === Number(boothId)
							  )
							: singleBooths.availableBooths.find(
									(booth) => Number(booth.boothId) === Number(boothId)
							  );

						const targetLocation = targetBooth?.boxPlacement;

						if (isInMulti) {
							await setBoothType("multi");
							setAvailableBooths(multiBooths.availableBooths);
							processAndGroupBooths(
								multiBooths.availableBooths,
								boothId,
								targetLocation
							);
						} else {
							await setBoothType("single");
							setAvailableBooths(singleBooths.availableBooths);
							processAndGroupBooths(
								singleBooths.availableBooths,
								boothId,
								targetLocation
							);
						}

						// Set these after processing
						setMyLocation(targetLocation);
						setSelectedBuilding(targetLocation);
						return;
					}
				}

				// Process current booth type
				setAvailableBooths(processedBooths);
				// Pass the scanned booth location if it exists
				const scannedLocation = booths.scannedBooth?.booth?.boxPlacement;
				processAndGroupBooths(processedBooths, boothId, scannedLocation);
			}
		} catch (error) {
			console.error("Failed to fetch available booths:", error);
			if (error.message === "No available booths found at this time") {
				setAvailableBooths([]);
			} else {
				env === "development"
					? setErrorMessage("Failed to fetch available booths")
					: setErrorMessage(
							"An error occurred while fetching the available booths",
							error.message
					  );
			}
		} finally {
			isInitialLoad.current = false;
			setPageLoading(false);
		}
	};

	// Helper function to process and group booths
	const processAndGroupBooths = (
		booths,
		targetBoothId,
		targetLocation = null
	) => {
		const groupedBuildings = booths.reduce((acc, booth) => {
			if (!booth?.boxPlacement) return acc;

			const building = booth.boxPlacement;
			const boxId = booth.boothId;

			const existingBuilding = acc.find((item) => item.building === building);
			if (existingBuilding) {
				existingBuilding.availableBoxes.push(boxId);
			} else {
				acc.push({
					building,
					availableBoxes: [boxId],
					hasTargetBooth: false,
				});
			}
			return acc;
		}, []);

		// Sort buildings alphabetically first
		groupedBuildings.sort((a, b) => a.building.localeCompare(b.building));

		// Use targetLocation if provided, otherwise use myLocation state
		const locationToUse = targetLocation || myLocation;

		// If we have a location, move that building to the top
		if (locationToUse) {
			const locationIndex = groupedBuildings.findIndex(
				(b) => b.building === locationToUse
			);
			if (locationIndex > -1) {
				const [locationBuilding] = groupedBuildings.splice(locationIndex, 1);
				groupedBuildings.unshift(locationBuilding);
			}
		}

		setGroupedBuildings(groupedBuildings);
	};

	// Remove boothType from dependency array to prevent re-fetching
	useEffect(() => {
		fetchBooths();
	}, [startTime, duration, boothType, locationId]);

	useEffect(() => {
		const getBoothLocation = async () => {
			try {
				const res = await getLocation(locationId);
				setBoothLocation(res.location);
			} catch (error) {
				console.error("Failed to fetch location:", error);
			}
		};
		getBoothLocation();
	}, [navigate]);

	const handleBuildingChange = (building) => {
		setSelectedBuilding(building);
		// Remove the automatic selection of the first box in the building
		setSelectedBooth(null);
	};
	const handleBoxChange = (boxNumber) => setSelectedBooth(boxNumber);

	const continueToStartSession = async () => {
		try {
			setPageLoading(true);
			const session = await temproraryReservation(
				selectedBooth,
				selectedBuilding,
				startTime,
				getEndTime(startTime, duration),
				locationId,
				date
			);
			setPageLoading(false);

			navigate(`/${locationId}/reservation/start-session`, {
				state: {
					sessionId: session.sessionId,
					boothId: selectedBooth,
					boothType: boothType,
					boxPlacement: selectedBuilding,
					time: transformStartTime(startTime),
					duration: duration,
					endTime: getEndTime(startTime, duration),
					date: date,
				},
			});
		} catch (error) {
			if (error.message === "The booth is no longer available") {
				await fetchBooths();
				displayErrorMessage("The booth is no longer available");
				return;
			} else {
				env === "development" && console.log("Error", error.message);
				displayErrorMessage("An error occurred while reserving the booth");
				return;
			}
		}
	};

	return (
		<section
			className="flex flex-col justify-center mb-20 pb-10 
    md:px-mdPagePadding md:mx-smPagePadding
    lg:px-lgPagePadding lg:mx-lgPagePadding 
    xl:px-xlPagePadding xl:mx-xlPagePadding
    "
		>
			{errorMessage && (
				<div className="fixed  top-0 left-0 right-0 m-8 text-black text-sm text-center py-3 z-50 border border-red-300 rounded-secondary bg-red-50 shadow-lg animate-slideInDown">
					<div className="flex flex-col items-center">
						<TriangleAlertIcon className="size-6" />
						<span className="mt-2">{errorMessage}</span>
					</div>
				</div>
			)}
			{/* -- Progress Stepper and Logo -- */}
			<div className="mt-10">
				<ProgressStepper currentStep={2} />
			</div>
			<div className="flex flex-col justify-center items-center px-2 py-8">
				<img src={logo} alt="logo" className="w-40 h-auto object-contain" />
			</div>

			{/* -- Booth Info -- */}
			<div className="border-t border-b border-gray-200 py-5 mx-smPagePadding flex flex-col justify-center items-center">
				<div className="flex justify-between items-center mb-2 text-sm">
					<div className="flex items-center gap-2 text-textSecondary">
						<span>
							{boothType ? capitalizeFirstLetter(boothType) : ""} booth #
							{selectedBooth || ""}
						</span>
						<div className="h-1 w-1 rounded-full bg-textSecondary "></div>
						<span className="">
							{date === 0 ? "Today" : "Tomorrow"} at{" "}
							{transformStartTime(startTime)} -{" "}
							{getEndTime(startTime, duration)}
						</span>
					</div>
				</div>
				<div className="flex items-center gap-3 text-textSecondary text-sm">
					<span>{boothLocation || ""}</span>
					<span>{selectedBuilding}</span>
				</div>
			</div>

			{/* -- Booth Type Toggle with pill style -- */}
			<div className="mx-smPagePadding py-6 md:px-6">
				<div className="relative flex items-center justify-center bg-gray-100 p-1 rounded-secondary w-full mx-auto">
					<div className="relative z-0 flex w-full">
						{/* Background pill that moves */}
						<div
							className={`absolute top-0 left-0 h-full w-1/2 bg-white rounded-secondary shadow-sm transition-transform duration-200 ease-in-out ${
								boothType === "single" ? "translate-x-0" : "translate-x-full"
							}`}
						/>

						{/* Single-person button */}
						<button
							className={`relative z-10 flex-1 py-2 px-4 text-center text-sm rounded-secondary transition-colors duration-200
                flex items-center justify-center gap-2
                ${
									boothType === "single"
										? "text-black font-medium"
										: "text-gray-500"
								}
              `}
							onClick={async () => {
								setSelectedBooth(null);
								setSelectedBuilding(null);
								await setBoothType("single");
							}}
						>
							<UserRoundIcon className="w-4 h-4" />
							Single-person
						</button>

						{/* Multi-person button */}
						<button
							className={`relative z-10 flex-1 py-2 px-4 text-center text-sm rounded-secondary transition-colors duration-200
                flex items-center justify-center gap-2
                ${
									boothType === "multi"
										? "text-black font-medium"
										: "text-gray-500"
								}
              `}
							onClick={async () => {
								setSelectedBooth(null);
								setSelectedBuilding(null);
								await setBoothType("multi");
							}}
						>
							<UsersRoundIcon className="w-4 h-4" />
							Multi-person
						</button>
					</div>
				</div>
			</div>

			{/* -- Pick a building -- */}
			<div className="flex flex-col gap-4 mx-smPagePadding">
				{groupedBuildings.length > 0 ? (
					<div className="relative">
						{/* Map through buildings */}
						{groupedBuildings.map((building) => (
							<div key={building.building} className="flex flex-col mb-4">
								<label className="cursor-pointer relative">
									{myLocation === building.building && (
										<div className="absolute -top-3 right-2 text-textSecondary text-xs bg-green-200 rounded-secondary py-2 px-4 flex items-center gap-1">
											<MapPinIcon className="w-4 h-4" />
											You are here!
										</div>
									)}
									<input
										type="radio"
										name="building"
										value={building.building}
										className="peer hidden"
										checked={
											selectedBuilding === building.building ||
											(myLocation === building.building && !selectedBuilding)
										}
										onChange={() => {
											handleBuildingChange(building.building);
										}}
									/>
									<div
										className="py-4 px-6 rounded-primary border border-gray-200 peer-checked:border-black hover:border-gray-300
                     hover:shadow-sm hover:bg-gray-50 transition-all duration-200 flex justify-between items-center"
									>
										<div>
											<h3 className="font-medium mb-1">{building.building}</h3>
											<p className="text-textSecondary text-sm">
												Available box #: {building.availableBoxes.join(", ")}
											</p>
										</div>
										<div>
											<span className="text-gray-400 text-sm">
												<ChevronDownIcon
													className={`w-4 h-4 ${
														selectedBuilding === building.building ||
														(myLocation === building.building &&
															!selectedBuilding)
															? "rotate-180 text-black"
															: ""
													} transition-all duration-400`}
												/>
											</span>
										</div>
									</div>
									<div className="grid grid-rows-[0fr] peer-checked:grid-rows-[1fr] transition-all duration-300">
										<div className="overflow-hidden">
											<div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 w-full pt-4">
												{building.availableBoxes.map((boxNumber) => {
													// Find the corresponding booth data
													const booth = availableBooths.find(
														(b) => b.boothId === boxNumber
													);
													return (
														<label
															key={boxNumber}
															className="cursor-pointer aspect-[1.5/1]"
														>
															<input
																type="radio"
																name="boxNumber"
																value={boxNumber}
																className="peer hidden"
																checked={selectedBooth === boxNumber}
																onChange={() =>
																	booth?.timeSlotStatus !== "locked" &&
																	booth?.timeSlotStatus !== "reserved" &&
																	handleBoxChange(boxNumber)
																}
															/>
															<div
																className={`w-full h-16 flex flex-col items-center justify-center rounded-primary border 
                                  ${
																		booth?.timeSlotStatus === "locked" ||
																		booth?.timeSlotStatus === "reserved"
																			? "border-gray-200 bg-gray-100 cursor-not-allowed opacity-60"
																			: "border-gray-200 peer-checked:border-black hover:border-gray-300 hover:shadow-sm hover:bg-gray-50"
																	}
                                  transition-all duration-200 relative`}
															>
																{boxNumber == boothId && (
																	<div className="absolute -top-3 right-[-12px] text-xs text-white rounded-secondary text-center px-2 py-1 bg-black flex items-center gap-1">
																		{booth?.timeSlotStatus === "locked"
																			? "Active Booking"
																			: booth?.timeSlotStatus === "reserved"
																			? "Booked"
																			: "Scanned"}
																	</div>
																)}
																<span
																	className={`text-xl ${
																		booth?.timeSlotStatus === "locked" ||
																		booth?.timeSlotStatus === "reserved"
																			? "text-gray-500"
																			: ""
																	}`}
																>
																	{boxNumber}
																</span>
															</div>
														</label>
													);
												})}
											</div>
										</div>
									</div>
								</label>
							</div>
						))}
					</div>
				) : pageLoading ? (
					""
				) : (
					<div className="flex flex-col gap-1 justify-center items-center text-center p-4 rounded-secondary">
						<div className="flex items-start">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-6"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
								/>
							</svg>

							<p className="text-black text-m ml-2">
								No available {boothType} booths found at this time...
							</p>
						</div>
					</div>
				)}
			</div>
			<ContinueBtn
				btnBack={true}
				disabled={!selectedBooth || !selectedBuilding}
				onClick={continueToStartSession}
				onBack={() => navigate(`/${locationId}/reservation/time-scheduler`)}
			/>

			{pageLoading && (
				<div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50">
					<div className="flex flex-col items-center">
						<div className="w-10 h-10 border-t-2 border-b-2 border-gray-900 rounded-full animate-spin"></div>
					</div>
				</div>
			)}
		</section>
	);
}
